import { useCallback, useEffect, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';

import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  APP_RELAYS,
  buildBlossomTags,
  buildNip65Tags,
  dedupeRelays,
  dedupeUrls,
  isStockAppRelayConfig,
  isUsingAppRelayFallback,
  normalizeNetworkUrl,
  type RelayEntry,
} from '@/lib/appRelays';
import { fetchUserNetworkLists } from '@/lib/networkListsFetch';
import type { UserNetworkLists } from '@/hooks/useUserNetworkLists';

export type NetworkListsApplyMode = 'replace' | 'merge';

/** Session-wide: avoid double auto-pull when multiple components mount the hook. */
const autoPulledForPubkey = new Set<string>();
let autoPullInFlight: string | null = null;

/**
 * Apply fetched NIP-65 / Blossom lists into local app config (the live pool).
 * Mount once near the app shell so auto-pull runs after login; Settings uses
 * the same hook for pull/publish actions.
 */
export function useNetworkListsSync() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();
  const queryClient = useQueryClient();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const clearMessages = useCallback(() => {
    setError(null);
    setStatus(null);
  }, []);

  const applyLists = useCallback(
    (
      lists: Pick<UserNetworkLists, 'relays' | 'blossomServers'>,
      mode: NetworkListsApplyMode = 'replace',
    ) => {
      const pubkey = user?.pubkey;
      updateConfig((c) => {
        const prevRelays = c.relayMetadata?.relays ?? config.relayMetadata.relays;
        const prevBlossom = c.blossomServers ?? config.blossomServers ?? [];

        let nextRelays: RelayEntry[];
        if (mode === 'merge') {
          const map = new Map<string, RelayEntry>();
          for (const r of prevRelays) {
            map.set(normalizeNetworkUrl(r.url), { ...r });
          }
          for (const r of lists.relays) {
            map.set(normalizeNetworkUrl(r.url), { ...r });
          }
          nextRelays = dedupeRelays([...map.values()]);
        } else {
          // Replace: prefer remote list when non-empty; keep local if remote empty
          nextRelays =
            lists.relays.length > 0
              ? dedupeRelays(lists.relays)
              : dedupeRelays(prevRelays);
        }

        // Never leave the pool empty — fall back to app defaults
        if (nextRelays.filter((r) => r.read || r.write).length === 0) {
          nextRelays = APP_RELAYS.relays.map((r) => ({ ...r }));
        }

        let nextBlossom: string[];
        if (mode === 'merge') {
          nextBlossom = dedupeUrls([...prevBlossom, ...lists.blossomServers]);
        } else {
          nextBlossom =
            lists.blossomServers.length > 0
              ? dedupeUrls(lists.blossomServers)
              : dedupeUrls(prevBlossom);
        }

        return {
          ...c,
          relayMetadata: {
            relays: nextRelays,
            updatedAt: Math.floor(Date.now() / 1000),
          },
          blossomServers: nextBlossom,
          ...(pubkey ? { networkListsPubkey: pubkey } : {}),
        };
      });
    },
    [config.blossomServers, config.relayMetadata.relays, updateConfig, user?.pubkey],
  );

  const pullFromRelays = useCallback(
    async (mode: NetworkListsApplyMode = 'replace'): Promise<UserNetworkLists | null> => {
      if (!user?.pubkey) {
        setError('Log in to pull your relay list');
        return null;
      }
      setBusy(true);
      setError(null);
      setStatus(null);
      try {
        // Separate kind queries + second hop on user's NIP-65 relays
        // (combined kinds+limit often drops 10063; Blossom often only on user relays)
        const lists: UserNetworkLists = await fetchUserNetworkLists({
          pubkey: user.pubkey,
          query: (filters, opts) => nostr.query(filters, opts),
          group: (urls) => {
            try {
              return nostr.group(urls) as { query: typeof nostr.query };
            } catch {
              return undefined;
            }
          },
          configuredRelayUrls: config.relayMetadata.relays.map((r) => r.url),
          // Raw WS hops need headroom (discovery + NIP-65, ~12s each)
          signal: AbortSignal.timeout(45_000),
        });

        queryClient.setQueryData(['user-network-lists', user.pubkey], lists);

        if (lists.relays.length === 0 && lists.blossomServers.length === 0) {
          setStatus(
            'No kind 10002 or 10063 found on connected relays (or your NIP-65 relays). Edit locally or publish from another client.',
          );
          // Still mark pubkey so we don't hammer auto-pull every mount
          updateConfig((c) => ({
            ...c,
            networkListsPubkey: user.pubkey,
          }));
          return lists;
        }

        applyLists(lists, mode);
        const parts: string[] = [];
        if (lists.relays.length) parts.push(`${lists.relays.length} relay(s)`);
        if (lists.blossomServers.length) {
          const src = lists.blossomSource ? ` via ${lists.blossomSource}` : '';
          parts.push(`${lists.blossomServers.length} Blossom server(s)${src}`);
        } else if (lists.blossomEvent) {
          parts.push(
            'Blossom event found but no server tags parsed — check kind 10063 ["server", url] tags',
          );
        } else {
          parts.push(
            '0 Blossom servers (kind 10063 not on discovery or your NIP-65 relays — try Publish from Ditto again, or add servers here)',
          );
        }
        setStatus(
          mode === 'merge'
            ? `Merged ${parts.join(' · ')} from your published lists`
            : `Applied ${parts.join(' · ')} from your published lists`,
        );
        return lists;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to pull network lists';
        setError(msg);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [
      applyLists,
      config.relayMetadata.relays,
      nostr,
      queryClient,
      updateConfig,
      user?.pubkey,
    ],
  );

  const publishRelays = useCallback(async () => {
    if (!user?.signer) {
      setError('Log in to publish your relay list');
      return false;
    }
    const relays = config.relayMetadata.relays.filter((r) => r.read || r.write);
    if (relays.length === 0) {
      setError('Add at least one read or write relay before publishing');
      return false;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const event = await user.signer.signEvent({
        kind: 10002,
        content: '',
        tags: buildNip65Tags(relays),
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event);
      setStatus('Published NIP-65 relay list (kind 10002)');
      void queryClient.invalidateQueries({ queryKey: ['user-network-lists', user.pubkey] });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to publish relay list');
      return false;
    } finally {
      setBusy(false);
    }
  }, [config.relayMetadata.relays, nostr, queryClient, user]);

  const publishBlossom = useCallback(async () => {
    if (!user?.signer) {
      setError('Log in to publish Blossom servers');
      return false;
    }
    const servers = dedupeUrls(config.blossomServers ?? []);
    if (servers.length === 0) {
      setError('Add at least one Blossom server before publishing');
      return false;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const event = await user.signer.signEvent({
        kind: 10063,
        content: '',
        tags: buildBlossomTags(servers),
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event);
      setStatus('Published Blossom server list (kind 10063)');
      void queryClient.invalidateQueries({ queryKey: ['user-network-lists', user.pubkey] });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to publish Blossom list');
      return false;
    } finally {
      setBusy(false);
    }
  }, [config.blossomServers, nostr, queryClient, user]);

  const publishAll = useCallback(async () => {
    const okRelays = await publishRelays();
    // publishRelays sets busy false; chain blossom
    if (config.blossomServers && config.blossomServers.length > 0) {
      await publishBlossom();
    }
    return okRelays;
  }, [config.blossomServers, publishBlossom, publishRelays]);

  /**
   * Once per login pubkey (session): if we haven't applied lists for this
   * account yet, or we're still on stock bootstrap defaults, pull NIP-65 + Blossom.
   */
  useEffect(() => {
    const pubkey = user?.pubkey;
    if (!pubkey) return;
    if (autoPulledForPubkey.has(pubkey) || autoPullInFlight === pubkey) return;

    const alreadyForThisUser = config.networkListsPubkey === pubkey;
    const onFallback = isUsingAppRelayFallback(config.relayMetadata);
    const stock = isStockAppRelayConfig(config.relayMetadata);

    if (alreadyForThisUser && !onFallback && !stock) {
      autoPulledForPubkey.add(pubkey);
      return;
    }

    autoPullInFlight = pubkey;
    autoPulledForPubkey.add(pubkey);
    void pullFromRelays('replace').finally(() => {
      if (autoPullInFlight === pubkey) autoPullInFlight = null;
    });
  }, [
    config.networkListsPubkey,
    config.relayMetadata,
    pullFromRelays,
    user?.pubkey,
  ]);

  return {
    busy,
    error,
    status,
    clearMessages,
    applyLists,
    pullFromRelays,
    publishRelays,
    publishBlossom,
    publishAll,
    usingAppFallback:
      isUsingAppRelayFallback(config.relayMetadata) ||
      isStockAppRelayConfig(config.relayMetadata),
  };
}
